import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AppConfig, ExamType, getAppByExamType } from '../config/apps.config';

const STORAGE_KEY = 'lid_exam_type';

interface AppSelectionContextType {
  examType: ExamType | null;
  selectedApp: AppConfig | null;
  setExamType: (examType: ExamType) => void;
  clearSelection: () => void;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

const AppSelectionContext = createContext<AppSelectionContextType | undefined>(undefined);

export const AppSelectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [examType, setExamTypeState] = useState<ExamType | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load exam type from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setExamTypeState(stored as ExamType);
      } catch (e) {
        console.error('Failed to parse stored exam type:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setIsInitialized(true);
  }, []);

  // For Leben in Deutschland, automatically set the exam type
  useEffect(() => {
    if (isInitialized && !examType) {
      // Auto-select Leben in Deutschland since it's the only option
      setExamTypeState('leben-in-deutschland');
      localStorage.setItem(STORAGE_KEY, 'leben-in-deutschland');
    }
  }, [isInitialized, examType]);

  const setExamType = (newExamType: ExamType) => {
    setExamTypeState(newExamType);
    localStorage.setItem(STORAGE_KEY, newExamType);
    setShowModal(false);
  };

  const clearSelection = () => {
    setExamTypeState(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const selectedApp = examType ? getAppByExamType(examType) ?? null : null;

  return (
    <AppSelectionContext.Provider
      value={{
        examType,
        selectedApp,
        setExamType,
        clearSelection,
        showModal,
        setShowModal,
      }}
    >
      {children}
    </AppSelectionContext.Provider>
  );
};

export const useAppSelection = (): AppSelectionContextType => {
  const context = useContext(AppSelectionContext);
  if (!context) {
    throw new Error('useAppSelection must be used within an AppSelectionProvider');
  }
  return context;
};

